/**
 * StreamLain Screenshot Hooks
 *
 * Hook system for extending clipboard screenshot functionality.
 * This file is PROPRIETARY and not subject to MPL 2.0 disclosure.
 *
 * The hooks are registered on window to allow minimal changes in
 * the original BlockSuite clipboard.ts file (~3 lines).
 */

import { FrameBlockModel } from '@blocksuite/affine-model';
import { isTopLevelBlock, matchModels } from '@blocksuite/affine-shared/utils';
import type { IBound } from '@blocksuite/global/gfx';
import { Bound } from '@blocksuite/global/gfx';
import type { GfxBlockElementModel } from '@blocksuite/std/gfx';
import type { GfxModel } from '@blocksuite/std/gfx';
import { GfxBlockElementModel as GfxBlockElementModelClass } from '@blocksuite/std/gfx';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Context passed to the screenshot render hook
 */
export interface ScreenshotRenderContext {
  nodeElements: GfxBlockElementModel[];
  bound: IBound;
  padding: number;
  ctx: CanvasRenderingContext2D;
  html2canvas: (
    element: HTMLElement,
    options?: Record<string, unknown>
  ) => Promise<HTMLCanvasElement>;
  html2canvasOption: Record<string, unknown>;
  /** Function to get block component by ID */
  getBlockComponent: (id: string) => HTMLElement | null;
  /** Function to get frame elements */
  getFrameElements: (
    frame: GfxBlockElementModel,
    includeInner: boolean
  ) => GfxModel[];
}

/**
 * Result of element splitting with additional categories
 */
export interface SplitElementsResult {
  otherBlocks?: GfxBlockElementModel[];
}

/**
 * Hook interface for screenshot functionality
 */
export interface StreamlainScreenshotHooks {
  /**
   * Render blocks to canvas using optimized approach
   * Returns true if handled, false to use default behavior
   */
  renderBlocks?: (context: ScreenshotRenderContext) => Promise<boolean>;

  /**
   * Additional element categories for splitElements
   */
  categorizeElement?: (element: GfxModel) => string | null;
}

// ============================================================================
// Hook Implementation
// ============================================================================

type RenderResult = {
  canvasData: HTMLCanvasElement;
  blockBound: { x: number; y: number; w: number; h: number };
  isInFrame: boolean;
};

/**
 * Render blocks using parallel html2canvas calls
 * This provides better UX than sequential calls by batching the work
 */
async function renderBlocksParallel(
  context: ScreenshotRenderContext
): Promise<boolean> {
  const {
    nodeElements,
    bound,
    padding,
    ctx,
    html2canvas,
    html2canvasOption,
    getBlockComponent,
    getFrameElements,
  } = context;

  // Collect all blocks to render (including those inside frames)
  const blocksToRender: {
    block: GfxBlockElementModel;
    isInFrame: boolean;
  }[] = [];

  for (const nodeElement of nodeElements) {
    blocksToRender.push({ block: nodeElement, isInFrame: false });

    if (matchModels(nodeElement, [FrameBlockModel])) {
      getFrameElements(nodeElement, false).forEach(ele => {
        if (isTopLevelBlock(ele)) {
          blocksToRender.push({
            block: ele as GfxBlockElementModel,
            isInFrame: true,
          });
        }
      });
    }
  }

  // PARALLEL: Run all html2canvas calls concurrently
  const renderPromises = blocksToRender.map(
    async ({ block, isInFrame }): Promise<RenderResult | null> => {
      const blockComponent = getBlockComponent(block.id);
      if (!blockComponent) {
        console.warn('Could not find block component for', block.id);
        return null;
      }

      const blockBound = Bound.deserialize(block.xywh);
      try {
        const canvasData = await html2canvas(
          blockComponent as HTMLElement,
          html2canvasOption
        );
        return { canvasData, blockBound, isInFrame };
      } catch (error) {
        console.warn('html2canvas failed for block', block.id, error);
        return null;
      }
    }
  );

  // Wait for all renders to complete
  const results = await Promise.all(renderPromises);

  // SEQUENTIAL: Draw all results to canvas (must be sequential for canvas API)
  for (const result of results) {
    if (!result) continue;
    const { canvasData, blockBound, isInFrame } = result;
    ctx.drawImage(
      canvasData,
      blockBound.x - bound.x + padding,
      blockBound.y - bound.y + padding,
      blockBound.w,
      isInFrame
        ? (blockBound.w / canvasData.width) * canvasData.height
        : blockBound.h
    );
  }

  return true;
}

/**
 * Categorize element for splitElements
 * Returns 'otherBlocks' for GfxBlockElementModel instances that aren't
 * already categorized (e.g., Database, Automation blocks)
 */
function categorizeElement(element: GfxModel): string | null {
  if (element instanceof GfxBlockElementModelClass) {
    return 'otherBlocks';
  }
  return null;
}

// ============================================================================
// Hook Registration
// ============================================================================

/**
 * Register StreamLain screenshot hooks on window
 * Call this during app initialization
 */
export function registerScreenshotHooks(): void {
  const hooks: StreamlainScreenshotHooks = {
    renderBlocks: renderBlocksParallel,
    categorizeElement: categorizeElement,
  };

  // Register on window for access from BlockSuite
  (window as any).__streamlainScreenshotHooks = hooks;
}

/**
 * Get registered screenshot hooks (for use in BlockSuite)
 */
export function getScreenshotHooks(): StreamlainScreenshotHooks | undefined {
  return (window as any).__streamlainScreenshotHooks;
}

// ============================================================================
// Type Declaration for Window
// ============================================================================

declare global {
  interface Window {
    __streamlainScreenshotHooks?: StreamlainScreenshotHooks;
  }
}
